import { Link, useLocation } from "react-router";
import { X, ChevronDown, ChevronRight, MoveLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Button from "../../ui/button";
import CountBadge from "./countBadge";
import VersionTypewriter from "../../common/versionTypewriter";
import { sidebarMenuItems } from "./sidebarLinks";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { checkPermission, hasAnyPermission } from "../../../utils/permissions";
import { getPendingRequestCounts } from "../../../store/admin/home-slice";
import { PendingRequestCounts } from "../../../utils/types";
import { getVersionString } from "../../../lib/version";
import { SidebarMenuItem } from "../../../utils/interface";

const ActiveIndicator = () => (
  <motion.span
    layoutId="sidebar-active"
    className="absolute inset-0 -z-10 rounded-lg bg-[#2b2213] border border-black/30 shadow-md"
  />
);

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar = ({ isOpen, toggleSidebar, onCollapseChange }: SidebarProps) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { permissions, user } = useAppSelector((state) => state.auth);
  const { pendingRequestCounts } = useAppSelector((state) => state.home);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const filterMenuItems = (items: SidebarMenuItem[]): SidebarMenuItem[] => {
    return items.reduce<SidebarMenuItem[]>((acc, item) => {
      if (item.requiredPermission) {
        const hasPermission = checkPermission(
          permissions,
          item.requiredPermission.section,
          item.requiredPermission.action,
        );
        if (!hasPermission) return acc;
      }

      if (item.requiredPermissions) {
        const hasPermission = hasAnyPermission(
          permissions,
          item.requiredPermissions.section,
          item.requiredPermissions.actions,
        );
        if (!hasPermission) return acc;
      }

      if (item.children) {
        const filteredChildren = filterMenuItems(item.children);
        if (filteredChildren.length === 0) return acc;
        // Build a new item instead of mutating the shared, exported
        // sidebarMenuItems config, which would otherwise leak this user's
        // permission-filtered children into every future render/session.
        acc.push({ ...item, children: filteredChildren });
        return acc;
      }

      acc.push(item);
      return acc;
    }, []);
  };

  const filteredMenuItems = filterMenuItems(sidebarMenuItems).flatMap((item) =>
    user?.role === "Compliance Officer" && item.id === "requests"
      ? item.children!.map((child) => ({
          ...child,
          label: `${child.label} Requests`,
        }))
      : [item],
  );

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [groupId],
    );
  };

  const handleGroupClick = (groupId: string) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setExpandedGroups([groupId]);
      onCollapseChange?.(false);
    } else {
      toggleGroup(groupId);
    }
  };

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    if (next) setExpandedGroups([]);
    onCollapseChange?.(next);
  };

  const isActive = (path: string) => location.pathname === path;
  const isGroupActive = (items: SidebarMenuItem[]) =>
    items.some((item) =>
      item.path
        ? isActive(item.path)
        : item.children?.some((child) => isActive(child.path as string)),
    );

  useEffect(() => {
    if (!hasInitialized && !isCollapsed) {
      const initialExpandedGroups: string[] = [];
      filteredMenuItems.forEach((item) => {
        if (item.children?.some((child) => isActive(child.path!))) {
          initialExpandedGroups.push(item.id);
        }
      });
      setExpandedGroups(initialExpandedGroups);
      setHasInitialized(true);
    }
  }, [filteredMenuItems, hasInitialized, isCollapsed]);

  useEffect(() => {
    dispatch(getPendingRequestCounts());
    const interval = setInterval(
      () => dispatch(getPendingRequestCounts()),
      600000,
    );
    return () => clearInterval(interval);
  }, [dispatch]);

  const getTotalPendingCount = () => {
    const counts = (pendingRequestCounts as PendingRequestCounts) || {};
    return (
      (counts.pendingWithdrawRequestCount || 0) +
      (counts.pendingUserInfoChangeRequestCount || 0)
    );
  };

  const getItemCount = (itemId: string) => {
    if (!pendingRequestCounts) return 0;
    switch (itemId) {
      case "withdrawal-request":
        return pendingRequestCounts.pendingWithdrawRequestCount || 0;
      case "user-change":
        return pendingRequestCounts.pendingUserInfoChangeRequestCount || 0;
      case "orders":
        return pendingRequestCounts.pendingEsouqOrderRequestCount || 0;
      case "website-orders":
        return pendingRequestCounts.pendingEsouqWebOrderRequestCount || 0;
      case "money-transactions":
        return pendingRequestCounts.latestStripeDepositsCount || 0;
      default:
        return 0;
    }
  };

  const getEsouqCount = () =>
    (pendingRequestCounts?.pendingEsouqOrderRequestCount || 0) +
    (pendingRequestCounts?.pendingEsouqWebOrderRequestCount || 0);

  const getStripeDepositCount = () =>
    pendingRequestCounts?.latestStripeDepositsCount || 0;

  return (
    <aside
      className={`fixed inset-y-0 left-0 ${
        isCollapsed ? "w-16" : "w-64"
      } bg-gradient-to-b from-[#C6AF7C] via-[#BBA473] to-[#AC9865] text-[#2b2213] border-r border-[#8a7a4f]
        shadow-[4px_0_24px_rgba(0,0,0,0.45)]
        flex flex-col h-screen transform transition-all duration-300 ease-in-out z-30
        ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
    >
      {/* Close btn - mobile only */}
      <Button
        className="absolute top-2.5 right-2.5 lg:hidden z-50 p-1.5 rounded-lg bg-black/10 hover:bg-black/20"
        variant="none"
        onClick={toggleSidebar}
        aria-label="Close menu"
      >
        <X
          size={16}
          className="text-black hover:text-white transition-colors"
        />
      </Button>

      {/* Logo */}
      <div
        className={`${isCollapsed ? "px-2 py-3" : "px-3 py-3"} border-b border-black/15 flex-shrink-0`}
      >
        <Link to="/" className="block">
          <div
            className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2.5"}`}
          >
            <div className="bg-[#2b2213] text-[#E8D5A3] w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm shadow-lg flex-shrink-0">
              S
            </div>
            {!isCollapsed && (
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-bold text-[#2b2213] tracking-tight leading-tight">
                  Save In Gold
                </span>
                <span className="text-[9px] text-[#5a4a2e] uppercase tracking-wider font-semibold">
                  Admin Panel
                </span>
              </div>
            )}
          </div>
        </Link>
      </div>

      {/* Nav */}
      <motion.nav layoutScroll className="sidebar-scroll flex-1 overflow-y-auto py-2 px-1.5 min-h-0">
        <ul className="space-y-0.5">
          {filteredMenuItems.map((item) => {
            // ── Leaf item ──
            if (!item.children) {
              return (
                <li key={item.id}>
                  <Link
                    to={item.path || "#"}
                    onClick={toggleSidebar}
                    title={isCollapsed ? item.label : undefined}
                    className={`relative isolate flex items-center rounded-lg border border-transparent transition-all duration-150 group
                      ${isCollapsed ? "justify-center w-10 h-10 mx-auto p-0" : "py-1.5 px-2.5 gap-2.5 justify-between"}
                      ${
                        isActive(item.path || "")
                          ? "text-[#E8D5A3] font-semibold"
                          : "hover:bg-black/10 text-[#3a2f1b] hover:text-[#2b2213]"
                      }`}
                  >
                    {isActive(item.path || "") && <ActiveIndicator />}
                    <div
                      className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2.5 flex-1 min-w-0"}`}
                    >
                      <span
                        className={`relative flex-shrink-0 ${isActive(item.path || "") ? "text-[#E8D5A3]" : "text-[#5a4a2e] group-hover:text-[#2b2213]"} transition-colors`}
                      >
                        {item.icon}
                        {isCollapsed && (
                          <CountBadge count={getItemCount(item.id)} dot />
                        )}
                      </span>
                      {!isCollapsed && (
                        <span className="truncate text-[13px] font-medium">
                          {item.label}
                        </span>
                      )}
                    </div>
                    {!isCollapsed && <CountBadge count={getItemCount(item.id)} />}
                  </Link>
                </li>
              );
            }

            // ── Group item ──
            const isExpanded = expandedGroups.includes(item.id) && !isCollapsed;
            const isCurrentGroupActive = isGroupActive(item.children);

            let groupCount = 0;
            if (item.id === "requests") groupCount = getTotalPendingCount();
            else if (item.id === "e-souq-manager") groupCount = getEsouqCount();
            else if (item.id === "reports")
              groupCount = getStripeDepositCount();

            return (
              <li key={item.id} className="space-y-0.5">
                <button
                  onClick={() => handleGroupClick(item.id)}
                  title={isCollapsed ? item.label : undefined}
                  aria-expanded={isExpanded}
                  aria-controls={`sidebar-group-${item.id}`}
                  className={`relative isolate flex items-center rounded-lg transition-all duration-150 border border-transparent group cursor-pointer
                    ${isCollapsed ? "justify-center w-10 h-10 mx-auto p-0" : "w-full py-1.5 px-2.5 gap-2.5 justify-between"}
                    ${
                      isCurrentGroupActive
                        ? "text-[#E8D5A3] font-semibold"
                        : "hover:bg-black/10 text-[#3a2f1b] hover:text-[#2b2213]"
                    }`}
                >
                  {isCurrentGroupActive && <ActiveIndicator />}
                  <div
                    className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2.5 flex-1 min-w-0"}`}
                  >
                    <span
                      className={`relative flex-shrink-0 ${isCurrentGroupActive ? "text-[#E8D5A3]" : "text-[#5a4a2e] group-hover:text-[#2b2213]"} transition-colors`}
                    >
                      {item.icon}
                      {isCollapsed && <CountBadge count={groupCount} dot />}
                    </span>
                    {!isCollapsed && (
                      <span className="truncate text-[13px] font-medium">
                        {item.label}
                      </span>
                    )}
                  </div>
                  {!isCollapsed && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <CountBadge count={groupCount} />
                      <span
                        className={`transition-transform duration-300 ${isCurrentGroupActive ? "text-[#E8D5A3]" : "text-[#5a4a2e] group-hover:text-[#2b2213]"}`}
                      >
                        {isExpanded ? (
                          <ChevronDown size={14} strokeWidth={2.5} />
                        ) : (
                          <ChevronRight size={14} strokeWidth={2.5} />
                        )}
                      </span>
                    </div>
                  )}
                </button>

                {/* Submenu — grid-rows 0fr→1fr animates to the content's real
                    height, so it can never clip a child the way a measured
                    max-height could. */}
                {!isCollapsed && (
                  <div
                    id={`sidebar-group-${item.id}`}
                    className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                      isExpanded
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <ul className="ml-2.5 pl-2 border-l border-black/20 space-y-0.5 pt-0.5 pb-0.5">
                        {item.children.map((child) => (
                        <li key={child.id}>
                          <Link
                            to={child.path!}
                            onClick={toggleSidebar}
                            className={`relative isolate flex items-center justify-between py-1 px-2 rounded-md transition-all duration-150 gap-1.5 group
                              ${
                                isActive(child.path!)
                                  ? "text-[#2b2213] font-semibold"
                                  : "text-[#3a2f1b]/80 hover:bg-black/10 hover:text-[#2b2213]"
                              }`}
                          >
                            {isActive(child.path!) && (
                              <motion.span
                                layoutId="sidebar-active-child"
                                className="absolute inset-0 -z-10 rounded-md bg-black/15 border-l-2 border-[#2b2213]"
                              />
                            )}
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <span
                                className={`flex-shrink-0 ${isActive(child.path!) ? "text-[#2b2213]" : "text-[#5a4a2e]/70 group-hover:text-[#2b2213]"} transition-colors`}
                              >
                                {child.icon}
                              </span>
                              <span className="truncate text-xs leading-tight">
                                {child.label}
                              </span>
                            </div>
                            <CountBadge count={getItemCount(child.id)} />
                          </Link>
                        </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </motion.nav>

      {/* Collapse button */}
      <div
        className={`border-t border-black/15 flex-shrink-0 ${isCollapsed ? "p-1.5" : "p-2"}`}
      >
        <Button
          variant="none"
          onClick={toggleCollapse}
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          className={`w-full flex items-center justify-center py-2 px-3 bg-[#2b2213] hover:bg-[#3a2f1b] text-[#E8D5A3] rounded-lg transition-all duration-200 border border-black/30 group ${isCollapsed ? "p-2" : ""}`}
        >
          <div
            className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2"}`}
          >
            <MoveLeft
              size={16}
              strokeWidth={2.5}
              className={`transform transition-transform duration-300 group-hover:scale-110 ${isCollapsed ? "rotate-180" : "rotate-0"}`}
            />
            {!isCollapsed && (
              <span className="text-xs font-medium">Collapse</span>
            )}
          </div>
        </Button>
        {!isCollapsed && (
          <p className="mt-1.5 text-center">
            <VersionTypewriter text={getVersionString()} colorClass="text-[#2b2213]" />
          </p>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
